import { useCallback, useMemo, useState } from 'react'
import { doc } from 'firebase/firestore'
import type { DocumentReference } from 'firebase/firestore'
import { useLocaleContext } from 'fbtee'
import { db } from '../lib/firebase'
import { setDoc } from '../lib/firestore'
import { useFirestoreDoc } from './useFirestore'
import { useAuth } from './useAuth'
import { useDeviceWeekStart } from './useDeviceWeekStart'
import { log } from '../lib/logger'
import { track } from '../lib/analytics'
import { resolveWeekStart, type WeekStartsOn } from '../lib/eventCalendar'
import type { UserPreferences, WeekStartPref } from '@shared/preferences'

const STORAGE_KEY = 'beacon_week_start'

function isPref(value: unknown): value is WeekStartPref {
  return value === 'auto' || value === 'monday' || value === 'sunday'
}

function readLocal(): WeekStartPref {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return isPref(raw) ? raw : 'auto'
  } catch {
    return 'auto'
  }
}

function writeLocal(pref: WeekStartPref) {
  try {
    localStorage.setItem(STORAGE_KEY, pref)
  } catch {
    // Not remembered past this visit; the choice still applies now.
  }
}

/**
 * Home's week start: the stored choice plus the day it resolves to. `auto`
 * follows the phone's setting in the native app and the app language elsewhere;
 * `autoWeekStartsOn` is what it resolves to, for labelling it. localStorage
 * first, so guests get it and it applies before Firestore answers; signed-in
 * users also sync it to `private/preferences` so it follows them across devices.
 */
export function useWeekStart(): {
  pref: WeekStartPref
  weekStartsOn: WeekStartsOn
  autoWeekStartsOn: WeekStartsOn
  setPref: (pref: WeekStartPref) => void
} {
  const { locale } = useLocaleContext()
  const { signedInUid } = useAuth()
  const deviceWeekStart = useDeviceWeekStart()
  const [pref, setPrefState] = useState<WeekStartPref>(readLocal)

  const ref = useMemo(
    () =>
      signedInUid
        ? (doc(db, 'users', signedInUid, 'private', 'preferences') as DocumentReference<UserPreferences>)
        : null,
    [signedInUid],
  )
  const { data: remote } = useFirestoreDoc<UserPreferences>(ref)

  // Only an explicit choice is ever written, so a remote value always wins.
  // Adopted during render, and only when it changes, so a resubscribe
  // delivering the same value can't undo a newer local choice.
  const remoteWeekStart = remote?.weekStart
  const remotePref = isPref(remoteWeekStart) ? remoteWeekStart : null
  const [trackedRemote, setTrackedRemote] = useState<WeekStartPref | null>(null)
  if (trackedRemote !== remotePref) {
    setTrackedRemote(remotePref)
    if (remotePref && remotePref !== pref) {
      setPrefState(remotePref)
      writeLocal(remotePref)
    }
  }

  const setPref = useCallback(
    (next: WeekStartPref) => {
      setPrefState(next)
      writeLocal(next)
      track('home_week_start_set', { week_start: next })
      if (ref) {
        void setDoc(ref, { weekStart: next }, { merge: true }).catch(() => {
          log('week-start', 'failed to sync week start preference')
        })
      }
    },
    [ref],
  )

  return {
    pref,
    weekStartsOn: resolveWeekStart(pref, locale, deviceWeekStart),
    autoWeekStartsOn: resolveWeekStart('auto', locale, deviceWeekStart),
    setPref,
  }
}
